import type { RecipeListItem } from '@recipes/contracts';
import {
  Button,
  Card,
  CardContent,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  EmptyState,
  Input,
  Spinner,
} from '@recipes/ui';
import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { MessageSquare, Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router';
import { ConfirmDialog } from '@/features/shared/confirm-dialog';
import { FormField } from '@/features/shared/form-field';
import { AiConsentNotice } from '@/features/shared/ai-consent-notice';
import { useAiConsent, useFeature } from '@/features/shared/hooks';
import { formatDate } from '@/features/shared/labels';
import { FeaturePageHeader, PageContainer } from '@/features/shared/page-header';
import { RecipeSearchList } from '@/features/shared/recipe-picker';
import { api } from '@/lib/api';
import { toastError } from '@/lib/errors';
import { queryKeys } from '@/lib/query-keys';
import { Alert } from '@recipes/ui';

export function ConversationsPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const aiEnabled = useFeature('ai');
  const consented = useAiConsent();
  const [createOpen, setCreateOpen] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);

  const list = useInfiniteQuery({
    queryKey: queryKeys.ai.conversations,
    queryFn: ({ pageParam }) => api.ai.conversations({ cursor: pageParam, limit: 20 }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });
  const items = list.data?.pages.flatMap((p) => p.items) ?? [];

  const remove = useMutation({
    mutationFn: (id: string) => api.ai.deleteConversation(id),
    onSuccess: () => {
      setDeleteId(null);
      void queryClient.invalidateQueries({ queryKey: queryKeys.ai.conversations });
    },
    onError: (e) => toastError(e),
  });

  return (
    <PageContainer className="max-w-3xl">
      <FeaturePageHeader
        title={t('assistant.title')}
        description={t('assistant.description')}
        actions={
          <Button
            onClick={() => setCreateOpen(true)}
            disabled={!aiEnabled || !consented}
            data-testid="new-conversation"
          >
            <Plus aria-hidden /> {t('assistant.new')}
          </Button>
        }
      />
      {!aiEnabled && <Alert variant="warning">{t('errors.FEATURE_DISABLED')}</Alert>}
      {aiEnabled && !consented && <AiConsentNotice />}
      {list.isLoading && <Spinner label={t('app.loading')} />}
      {!list.isLoading && items.length === 0 && (
        <EmptyState
          icon={<MessageSquare />}
          title={t('assistant.emptyTitle')}
          description={t('assistant.emptyBody')}
        />
      )}
      <ul className="space-y-2">
        {items.map((c) => (
          <li key={c.id}>
            <Card className="hover:bg-accent/40">
              <CardContent className="flex items-center gap-3 p-3 md:p-4">
                <Link
                  to={`/assistant/${c.id}`}
                  className="min-w-0 flex-1 rounded outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  <span className="block truncate font-medium">
                    {c.title || t('assistant.untitled')}
                  </span>
                  {c.lastMessagePreview && (
                    <span className="block truncate text-sm text-muted-foreground">
                      {c.lastMessagePreview}
                    </span>
                  )}
                  <span className="block text-xs text-muted-foreground">
                    {formatDate(c.updatedAt, i18n.language, true)}
                  </span>
                </Link>
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label={t('assistant.delete')}
                  onClick={() => setDeleteId(c.id)}
                >
                  <Trash2 aria-hidden />
                </Button>
              </CardContent>
            </Card>
          </li>
        ))}
      </ul>
      {list.hasNextPage && (
        <div className="mt-4 text-center">
          <Button
            variant="outline"
            loading={list.isFetchingNextPage}
            onClick={() => void list.fetchNextPage()}
          >
            {t('common.loadMore')}
          </Button>
        </div>
      )}
      <NewConversationDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        onCreated={(id) => navigate(`/assistant/${id}`)}
      />
      <ConfirmDialog
        open={Boolean(deleteId)}
        onOpenChange={(o) => !o && setDeleteId(null)}
        title={t('assistant.deleteTitle')}
        description={t('assistant.deleteBody')}
        destructive
        confirmLabel={t('common.delete')}
        loading={remove.isPending}
        onConfirm={() => {
          if (deleteId) remove.mutate(deleteId);
        }}
      />
    </PageContainer>
  );
}

export function NewConversationDialog({
  open,
  onOpenChange,
  onCreated,
  recipeId: presetRecipeId,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated: (id: string) => void;
  recipeId?: string | null;
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [recipe, setRecipe] = useState<RecipeListItem | null>(null);
  const create = useMutation({
    mutationFn: () =>
      api.ai.createConversation({
        recipeId: presetRecipeId ?? recipe?.id ?? null,
        title: title.trim() || undefined,
        initialMessage: message.trim() || undefined,
      }),
    onSuccess: (conversation) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.ai.conversations });
      onOpenChange(false);
      onCreated(conversation.id);
    },
    onError: (e) => toastError(e),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent closeLabel={t('common.close')} fullscreenOnMobile>
        <DialogHeader>
          <DialogTitle>{t('assistant.new')}</DialogTitle>
          <DialogDescription>{t('assistant.newDescription')}</DialogDescription>
        </DialogHeader>
        <form
          className="space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            create.mutate();
          }}
        >
          <FormField label={t('assistant.fields.title')} optionalText={t('common.optional')}>
            <Input
              value={title}
              maxLength={120}
              onChange={(e) => setTitle(e.target.value)}
              data-testid="conversation-title"
            />
          </FormField>
          <FormField
            label={t('assistant.fields.initialMessage')}
            optionalText={t('common.optional')}
          >
            <Input value={message} maxLength={8000} onChange={(e) => setMessage(e.target.value)} />
          </FormField>
          {!presetRecipeId && (
            <div>
              <p className="mb-1 text-sm font-medium">{t('assistant.fields.recipe')}</p>
              <p className="mb-2 text-xs text-muted-foreground">
                {t('assistant.fields.recipeHint')}
              </p>
              <RecipeSearchList
                multiple={false}
                selected={recipe ? [recipe.id] : []}
                onToggle={(r) => setRecipe((prev) => (prev?.id === r.id ? null : r))}
              />
            </div>
          )}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" loading={create.isPending} data-testid="conversation-create">
              {t('assistant.start')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
